import React, { useState, useEffect} from "react";
import { apiClient } from "../api_client";
import { InfoTooltip } from "./InfoTooltip";

export default function BibGroupsForm({ initialBibGroups = [], initialBibRules = [], eventId, eventInfoData, onSuccess }) {
  const [bibGroups, setBibGroups] = useState(initialBibGroups);
  const [bibRules, setBibRules] = useState(initialBibRules);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [catList, setCatList] = useState([]);
  const [updatedEventId, setEventId] = useState(eventId);

  useEffect(() => {
    defaultBibs();
    refresh();
  }, [eventInfoData.bib_group_total]);

  const refresh = async () => {
    try {
      const result = await apiClient.bibs.getCategories();
      setCatList(result.cat_list || []);
    } catch (err) {
      console.error("Failed to fetch bib groups:", err);
    }
  }
 
  const addBibGroup = () => {
    setBibGroups([
      ...bibGroups,
      { id: Date.now(), name: "", ranges: [{ lower_range: "", upper_range: ""}] },
    ]);
  };

  const removeBibGroup = (groupId) => {
    setBibGroups(bibGroups.filter((group) => group.id !== groupId));
  };

  const updateBibGroup = (id, field, value) => {
    setBibGroups(
      bibGroups.map((group) =>
        group.id === id ? { ...group, [field]: value } : group
      )
    );
  };

  const getLetter = (index) => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    return alphabet[index % alphabet.length];
  };

  const defaultBibs = () => {
    const total = parseInt(eventInfoData.bib_group_total || 0, 10) - bibGroups.length;
    if (!total || total <= 0) return;

    const groups = [];
    for (let i = 0; i < total; i++) {
      groups.push({
        id: Date.now() + i,
        name: getLetter(i),
        ranges: [{
          lower_range: "",
          upper_range: "",
        }],
      });
    }
    setBibGroups([...bibGroups, ...groups]);
  };

  const skipCustomSetup = async () => {
    try {      
      await apiClient.bibs.skipCustomSetup(updatedEventId).then(() => {
        showToast("Skipped custom bib setup. Default bib group created.", "success");
        if (onSuccess) {
          onSuccess(null, null);
        }
      });
    } catch (err) {
      console.error("Failed to skip custom setup:", err);
      showToast("Failed to skip custom setup. Please try again.", "error");
    }
  }

  const updateBibRule = (cat, field, value) => {
    setBibRules((prev) => ({
      ...prev,
      [cat]: {
        ...prev[cat],
        [field]: value,
      },
    }));
  };

  const setAllSFToGroup = (group) => {
    setBibRules((prev) => {
      const updated = { ...prev };
      catList
        .filter((c) => c.name.startsWith("WV") || c.name.startsWith("WS"))
        .forEach((c) => {
          updated[c.id] = { ...(updated[c.id] || {}), groupId: group.id };
        });
      return updated;
    });
  };

  const setAllSMToGroup = (group) => {
    setBibRules((prev) => {
      const updated = { ...prev };
      catList
        .filter((c) => c.name.startsWith("MV") || c.name.startsWith("MS"))
        .forEach((c) => {
          updated[c.id] = { ...(updated[c.id] || {}), groupId: group.id };
        });
      return updated;
    });
  };

  const setAllJFToGroup = (group) => {
    setBibRules((prev) => {
      const updated = { ...prev };
      catList
        .filter((c) => c.name.startsWith("WU"))
        .forEach((c) => {
          updated[c.id] = { ...(updated[c.id] || {}), groupId: group.id };
        });
      return updated;
    });
  };

  const setAllJMToGroup = (group) => {
    setBibRules((prev) => {
      const updated = { ...prev };
      catList
        .filter((c) => c.name.startsWith("MU"))
        .forEach((c) => {
          updated[c.id] = { ...(updated[c.id] || {}), groupId: group.id };
        });
      return updated;
    });
  };

  const bibSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await apiClient.bibs.create(updatedEventId, bibGroups, bibRules);

      showToast('Bib groups saved successfully!', "success");
      setEventId(response.id);
      if (onSuccess) {
        onSuccess(bibGroups, bibRules);
      }
      
    } catch (error) {
      setIsSubmitting(false);
      window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        showToast(error, "error");
        return;
    } finally {
        setIsSubmitting(false);
      }
  };

  const addRange = (groupId) => {
    setBibGroups(prev =>
      prev.map(group =>
        group.id === groupId
          ? { ...group, ranges: [...group.ranges, { lower_range: "", upper_range: "" }] }
          : group
      )
    );
  };

  const updateRange = (groupId, rangeIndex, field, value) => {
    setBibGroups(prev =>
      prev.map(group => {
        if (group.id !== groupId) return group;
        const newRanges = group.ranges.map((range, idx) =>
          idx === rangeIndex ? { ...range, [field]: value } : range
        );
        return { ...group, ranges: newRanges };
      })
    );
  };

  const removeRange = (groupId, rangeIndex) => {
    setBibGroups(prev =>
      prev.map(group => {
        if (group.id !== groupId) return group;
        const newRanges = group.ranges.filter((_, idx) => idx !== rangeIndex);
        return { ...group, ranges: newRanges };
      })
    );
  };

  return (
    <form onSubmit={bibSubmit}>
      {onSuccess && (
        <div>
          <button
            type="button"
            className="btn btn-secondary mt-2"
            onClick={() => 
              skipCustomSetup()
            }
          >
            Skip Custom bib set up
          </button>
          <InfoTooltip>
            <p>Skipping this will make a default bib group that assigns all entrants to the same group.</p>
          </InfoTooltip>
        </div>
      )}
      <div className="d-flex gap-4 mt-3">
        {/* Sidebar */}
        <div className="flex-shrink-0 w-25">
          <ul className="list-group">
            {bibGroups.map((group) => (
              <div key={group.id} className="card mb-4">
                <div className="card-body">
                  <h5>Categories</h5>
                  <input
                    type="text"
                    name="group-name"
                    value={group.name}
                    onChange={(e) =>
                      updateBibGroup(group.id, "name", e.target.value)
                    }
                    className="form-control mb-2"
                    placeholder="Group Name"
                    required={true}
                  />
                  {group.ranges.map((range, idx) => (
                    <div key={idx} className="d-flex gap-2 mb-2">
                      <input
                        type="number"
                        value={range.lower_range}
                        name="lower-bib"
                        onChange={(e) => updateRange(group.id, idx, "lower_range", e.target.value)}
                        className="form-control"
                        placeholder="Lower Bib"
                        required
                      />
                      <input
                        type="number"
                        value={range.upper_range}
                        name="upper-bib"
                        onChange={(e) => updateRange(group.id, idx, "upper_range", e.target.value)}
                        className="form-control"
                        placeholder="Upper Bib"
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => removeRange(group.id, idx)}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-primary mt-1"
                    onClick={() => addRange(group.id)}
                  >
                    Add Range
                  </button>

                  <div className="d-flex gap-2 flex-wrap mt-2">
                    <button
                      type="button"
                      name="set-jm"
                      className="btn btn-sm btn-primary"
                      onClick={() => setAllJMToGroup(group)}
                    >
                      Set All Junior Males
                    </button>

                    <button
                      type="button"
                      name="set-sm"
                      className="btn btn-sm btn-primary"
                      onClick={() => setAllSMToGroup(group)}
                    >
                      Set All Senior Males
                    </button>

                    <button
                      type="button"
                      name="set-jf"
                      className="btn btn-sm btn-primary"
                      onClick={() => setAllJFToGroup(group)}
                    >
                      Set All Junior Females
                    </button>

                    <button
                      type="button"
                      name="set-sf"
                      className="btn btn-sm btn-primary"
                      onClick={() => setAllSFToGroup(group)}
                    >
                      Set All Senior Females
                    </button>

                    <button
                      type="button"
                      name="delete-button"
                      className="btn btn-outline-danger"
                      onClick={() => removeBibGroup(group.id)}
                    >
                      Delete Group
                    </button>                    
                  </div>
                </div>
              </div>
            ))}
          </ul>
          <button 
            type="button"
            className="btn btn-outline-secondary mb-3" 
            onClick={addBibGroup}
          >
            Add Bib Group
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-grow-1">
          <h6>Bib Rules</h6>
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Category</th>
                <th>Age to Use</th>
                <th>Group</th>
              </tr>
            </thead>
            <tbody>
              {catList.map((cat) => (
                <tr key={cat.id}>
                  <td>{cat.name}</td>
                  <td>
                    <select
                      value={bibRules[cat.id]?.age_to_use || ""}
                      onChange={(e) =>
                        updateBibRule(cat.id, "age_to_use", e.target.value)
                      }
                      className="form-control"
                    >
                      <option value="race_day">Race Day</option>
                      <option value="end_of_year">End Of Year</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={bibRules[cat.id]?.groupId || ""}
                      onChange={(e) =>
                        updateBibRule(cat.id, "groupId", Number(e.target.value))
                      }
                      className="form-control"
                    >
                      <option value="">--Not Racing--</option>
                      {bibGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name || "(unnamed)"}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              zIndex: 1000,
            }}
          >
            <button
              type="submit"
              id="bib-group-submit"
              className="btn btn-success btn-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Bib Groups'}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}